/**
 * EntityLookup Component
 * Generic lookup/search component for entity relationships (Dynamics CRM pattern)
 * 
 * Features:
 * - Real-time search with debounce
 * - Keyboard navigation
 * - Loading states
 * - Clear selection
 * - Custom render for options
 * - Integrates with react-hook-form
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';

export interface EntityOption {
  value: string;
  label: string;
  subtitle?: string;
  metadata?: Record<string, any>;
}

export interface EntityLookupProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  onSearch: (query: string) => Promise<EntityOption[]>;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  searchPlaceholder?: string;
  allowClear?: boolean;
  renderOption?: (option: EntityOption) => React.ReactNode;
  minSearchLength?: number;
  /** Initial label to display when value is set but not yet resolved from search */
  initialLabel?: string;
}

export function EntityLookup({
  value,
  onChange,
  onSearch,
  placeholder = 'Seleccionar...',
  emptyMessage = 'No se encontraron resultados',
  disabled = false,
  className,
  searchPlaceholder = 'Buscar...',
  allowClear = true,
  renderOption,
  minSearchLength = 2,
  initialLabel,
}: EntityLookupProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState<EntityOption | null>(null);
  
  const debouncedSearch = useDebounce(searchQuery, 300);
  const commandRef = useRef<HTMLDivElement>(null);

  // Load initial options when opening
  useEffect(() => {
    if (open && options.length === 0) {
      handleSearch('');
    }
  }, [open]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedSearch.length >= minSearchLength || debouncedSearch === '') {
      handleSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // Update selected label when value changes
  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      return;
    }

    setSelectedOption((current) => {
      // Keep existing option if it already matches the current value
      if (current?.value === value) {
        const match = options.find((opt) => opt.value === value);
        return match ?? current;
      }

      const match = options.find((opt) => opt.value === value);
      if (match) {
        return match;
      }

      // Fallback: preserve previous label if available, otherwise use initialLabel or value
      if (current && current.value === value) {
        return current;
      }

      return current ?? { value, label: initialLabel || value };
    });
  }, [value, options, initialLabel]);

  const handleSearch = useCallback(async (query: string) => {
    try {
      setLoading(true);
      const results = await onSearch(query);
      setOptions(results);
    } catch (error) {
      console.error('Search error:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [onSearch]);

  const handleSelect = (selectedValue: string) => {
    const newValue = selectedValue === value ? undefined : selectedValue;

    if (newValue) {
      const selected = options.find((opt) => opt.value === newValue);
      setSelectedOption(selected ?? { value: newValue, label: newValue });
    } else {
      setSelectedOption(null);
    }

    onChange(newValue);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setSelectedOption(null);
    setSearchQuery('');
  };

  const renderDefaultOption = (option: EntityOption) => (
    <div className="flex flex-col">
      <span className="font-medium">{option.label}</span>
      {option.subtitle && (
        <span className="text-xs text-muted-foreground">{option.subtitle}</span>
      )}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">
            {selectedOption?.label || value || placeholder}
          </span>
          <div className="flex items-center gap-1 ml-2">
            {allowClear && value && !disabled && (
              <X
                className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command ref={commandRef} shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Buscando...</span>
              </div>
            ) : options.length === 0 ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={handleSelect}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {renderOption ? renderOption(option) : renderDefaultOption(option)}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
